import { ImageFromServer } from '@/components/images/ImageFromServer';
import { ExerciseDTO, ExercisePrerequisiteDTO } from '@/generated';
import { Box, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, Paper, Stack, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { findRelatedExercises } from '../exercise-utils';

type CopyPrerequisitesModalProps = {
  show: boolean;
  progressionExerciseIds: string[];
  regressionExerciseIds: string[];
  exercises: ExerciseDTO[];
  onHide: () => void;
  onCopy: (prerequisites: ExercisePrerequisiteDTO[]) => void;
};

const prerequisiteDetails = (prerequisite: ExercisePrerequisiteDTO) => {
  const values = [
    prerequisite.minSets !== undefined ? `${prerequisite.minSets} sets` : undefined,
    prerequisite.minReps !== undefined ? `${prerequisite.minReps} reps` : undefined,
    prerequisite.minDurationSeconds !== undefined ? `${prerequisite.minDurationSeconds} sec` : undefined,
    prerequisite.minLoadKg !== undefined ? `${prerequisite.minLoadKg} kg` : undefined,
  ];
  return values.filter(Boolean).join(' · ') || 'No requirements configured';
};

export const CopyPrerequisitesModal = (props: CopyPrerequisitesModalProps) => {
  const [sourceExerciseId, setSourceExerciseId] = useState<string>();
  const [selectedPrerequisiteIds, setSelectedPrerequisiteIds] = useState<string[]>([]);
  const sourceExercises = useMemo(
    () => findRelatedExercises(props.progressionExerciseIds, props.regressionExerciseIds, props.exercises),
    [props.progressionExerciseIds, props.regressionExerciseIds, props.exercises],
  );
  const sourceExercise = sourceExercises.find((exercise) => exercise.id === sourceExerciseId);

  useEffect(() => {
    if (props.show) {
      setSourceExerciseId(undefined);
      setSelectedPrerequisiteIds([]);
    }
  }, [props.show]);

  const selectSourceExercise = (exercise: ExerciseDTO) => {
    setSourceExerciseId(exercise.id);
    setSelectedPrerequisiteIds(exercise.prerequisites.map((prerequisite) => prerequisite.exerciseId));
  };

  const togglePrerequisite = (exerciseId: string) => {
    setSelectedPrerequisiteIds((current) => (current.includes(exerciseId) ? current.filter((selectedId) => selectedId !== exerciseId) : [...current, exerciseId]));
  };

  return (
    <Dialog open={props.show} onClose={props.onHide} fullWidth maxWidth="md">
      <DialogTitle>Copy prerequisites</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'minmax(220px, 1fr) minmax(280px, 1.4fr)' }, gap: 3, minHeight: 300 }}>
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Progressions and regressions
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: 1 }}>
              {sourceExercises.map((exercise) => (
                <Paper
                  key={exercise.id}
                  component="button"
                  type="button"
                  aria-pressed={sourceExerciseId === exercise.id}
                  onClick={() => selectSourceExercise(exercise)}
                  variant="outlined"
                  sx={{
                    borderColor: sourceExerciseId === exercise.id ? 'common.white' : 'divider',
                    bgcolor: sourceExerciseId === exercise.id ? 'action.selected' : 'background.paper',
                    color: 'text.primary',
                    cursor: 'pointer',
                    p: 1,
                    minWidth: 0,
                  }}
                >
                  <ImageFromServer id={exercise.imageId} thumbnail alt="" sx={{ width: 72, height: 72, mx: 'auto', borderRadius: 1 }} />
                  <Typography variant="caption" component="div" sx={{ mt: 0.5, overflowWrap: 'anywhere' }}>
                    {exercise.name}
                  </Typography>
                </Paper>
              ))}
            </Box>
            {!sourceExercises.length && <Typography color="text.secondary">No connected progressions or regressions found.</Typography>}
          </Box>

          <Box sx={{ borderLeft: { sm: 1 }, borderColor: { sm: 'divider' }, pl: { sm: 3 } }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              {sourceExercise ? `Prerequisites of ${sourceExercise.name}` : 'Prerequisites'}
            </Typography>
            {!sourceExercise && <Typography color="text.secondary">Select an exercise on the left.</Typography>}
            {sourceExercise && !sourceExercise.prerequisites.length && <Typography color="text.secondary">This exercise has no prerequisites.</Typography>}
            <Stack spacing={1}>
              {sourceExercise?.prerequisites.map((prerequisite) => {
                const prerequisiteExercise = props.exercises.find((exercise) => exercise.id === prerequisite.exerciseId);
                const selected = selectedPrerequisiteIds.includes(prerequisite.exerciseId);
                return (
                  <Paper
                    key={prerequisite.exerciseId}
                    component="button"
                    type="button"
                    variant="outlined"
                    aria-pressed={selected}
                    onClick={() => togglePrerequisite(prerequisite.exerciseId)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      width: '100%',
                      p: 1,
                      color: 'text.primary',
                      textAlign: 'left',
                      cursor: 'pointer',
                      borderColor: selected ? 'common.white' : 'divider',
                      bgcolor: selected ? 'action.selected' : 'background.paper',
                    }}
                  >
                    <Checkbox checked={selected} tabIndex={-1} disableRipple sx={{ color: 'common.white', '&.Mui-checked': { color: 'common.white' } }} />
                    <ImageFromServer id={prerequisiteExercise?.imageId} thumbnail alt="" sx={{ width: 48, height: 48, flexShrink: 0, borderRadius: 1 }} />
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600}>
                        {prerequisiteExercise?.name ?? prerequisite.exerciseId}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {prerequisiteDetails(prerequisite)}
                      </Typography>
                    </Box>
                  </Paper>
                );
              })}
            </Stack>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onHide}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!sourceExercise || !selectedPrerequisiteIds.length}
          onClick={() => {
            if (!sourceExercise) return;
            props.onCopy(sourceExercise.prerequisites.filter((prerequisite) => selectedPrerequisiteIds.includes(prerequisite.exerciseId)));
          }}
        >
          Accept
        </Button>
      </DialogActions>
    </Dialog>
  );
};
